import { BehaviorSubject, Subject, filter, of, take, takeUntil, tap } from "rxjs";
import { LocalItemStore, RemoteItemStore } from "../classes";
import { errorMessages } from "../shared/errorMessages";
import { AllowedKeyType } from "../types";

export type RemoteItemChangedSideffect = 'updateLocal' | 'updateRemote'

export interface IRemoteItemConfig<TModel extends { [key: string]: any }, TMapped extends object = TModel, TKeyType extends AllowedKeyType = string> {
  customStore: RemoteItemStore<TModel, TKeyType>,
  map?: (item: TModel) => TMapped,
  unMap?: (mappedItem: TMapped) => TModel,
  dontSync?: boolean
}

export class RemoteItemService<TModel extends { [key: string]: any }, TMapped extends object = TModel, TKeyType extends AllowedKeyType = string> {

  protected readonly _destroy = new Subject<void>();

  readonly keyValueSubject = new BehaviorSubject<TKeyType>(undefined)
  get keyValue() {
    return this.keyValueSubject.value;
  }
  set keyValue(value) {
    this.keyValueSubject.next(value)
    this._customStore.keyValue = value;
  }
  get canUpdateAndRemove() {
    return Boolean(this.keyValue)
  }
  get canInsert() {
    return Boolean(!this.keyValue)
  }

  readonly remoteDataSubject = new BehaviorSubject<TModel | null>(null);
  set remoteData(value: TModel) {
    this.remoteDataSubject.next(value);
  }
  get remoteData() {
    return this.remoteDataSubject.value
  }

  readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly insertingSubject = new BehaviorSubject<boolean>(false);
  readonly updatingSubject = new BehaviorSubject<boolean>(false);
  readonly removingSubject = new BehaviorSubject<boolean>(false);

  localItemStore: LocalItemStore<TModel, TMapped> = new LocalItemStore<TModel, TMapped>();


  protected _customStore: RemoteItemStore<TModel, TKeyType>
  private _map?: (item: TModel) => TMapped
  private _unMap?: (mappedItem: TMapped) => TModel


  setConfig(config: IRemoteItemConfig<TModel, TMapped, TKeyType>) {
    this._customStore = config.customStore;
    this._map = config.map || ((item: TModel) => item as unknown as TMapped);
    this._unMap = config.unMap || ((item: TMapped) => item as unknown as TModel);

    this.keyValue = this._customStore.keyValue;
    this.localItemStore.map = this._map;

    this.keyValueSubject
      .pipe(
        takeUntil(this._destroy),
        filter(Boolean)
      )
      .subscribe((key) => this._customStore.keyValue = key);

    if (!config.dontSync) {

      this.keyValueSubject
        .pipe(
          takeUntil(this._destroy),
          filter(Boolean)
        )
        .subscribe(keyValue => {
          this.load().pipe(take(1), takeUntil(this._destroy)).subscribe();
        });
    }


    this.remoteDataSubject
      .pipe(
        takeUntil(this._destroy),
      )
      .subscribe(data => {
        this.localItemStore.replace(data)
      })
  }

  load() {

    this.loadingSubject.next(true)

    return this._customStore.load()
      .pipe(
        tap(() => this.loadingSubject.next(false)),
        tap((data) => this.remoteDataSubject.next(data))
      )
  }

  insert(insertJSON: Partial<TMapped>) {

    if (!this.canInsert) {
      throw { message: errorMessages.INSERT_ALREAD_INSERTED };
    }

    this.insertingSubject.next(true)

    const unmapped = this._unMap(insertJSON as TMapped);
    const insertedObs = this._customStore.insert(unmapped);

    return insertedObs
      .pipe(
        tap((key) => {
          this.insertingSubject.next(false);
        }),
        tap((key) => {
          this.keyValue = key;
          this.load().pipe(take(1), takeUntil(this._destroy)).subscribe();
        })
      )
  }

  update(updateJSON: Partial<TMapped>, sideffect: RemoteItemChangedSideffect) {

    if (!this.canUpdateAndRemove) {
      throw { errorMessages: errorMessages.KEY_DOES_NOT_HAVE_A_VALUE };
    }

    const prev = this.remoteData;
    const unmapped = this._unMap(updateJSON as TMapped);

    if (JSON.stringify(prev) === JSON.stringify(unmapped)) {
      return of(unmapped);
    }

    this.updatingSubject.next(true);
    const updateObs = this._customStore.update(unmapped);


    return updateObs
      .pipe(
        tap(() =>
          this.updatingSubject.next(false)
        ),
        tap(() => {
          if (sideffect === 'updateLocal') {
            this.localItemStore.replaceDiffs(unmapped);
            return;
          }

          this.load().pipe(take(1), takeUntil(this._destroy)).subscribe();
        }
        )
      )
  }


  remove() {

    if (!this.canUpdateAndRemove) {
      throw { message: errorMessages.KEY_DOES_NOT_HAVE_A_VALUE }
    }

    this.removingSubject.next(true);

    const removeObs = this._customStore.remove();

    return removeObs
      .pipe(
        tap(() => this.removingSubject.next(false)),
        tap(() => {
          this.remoteDataSubject.next(null)
          this.keyValue = undefined;
        })
      )
  }

  dispose() {

    this._destroy.complete();
    this.keyValueSubject.complete();
    this.remoteDataSubject.complete();
    this.loadingSubject.complete();
    this.insertingSubject.complete();
    this.updatingSubject.complete();
    this.removingSubject.complete();
  }


}
